import { useState } from "react";
import { Phone, Mail, Clock, MapPin, Printer, Send, Facebook, Instagram, Twitter, Youtube } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Reveal } from "../components/Reveal";
import { company, services } from "../mock";

const initialForm = { name: "", email: "", phone: "", service: "", message: "" };
const SUBMIT_DELAY_MS = 800;

// In-memory store for mock submissions. Intentionally NOT persisted to
// localStorage/sessionStorage to avoid storing personal contact details on the client.
const inquiries = [];

// Validate required fields; returns an error message or null
function validateForm(form) {
  if (!form.name || !form.email || !form.message) {
    return "Please fill in your name, email and message.";
  }
  return null;
}

// Record an inquiry in memory only (mock backend stand-in)
function recordInquiry(form) {
  inquiries.push({ ...form, date: new Date().toISOString() });
}

const infoItems = [
  { icon: Phone, label: "Call Us", value: company.phone, href: `tel:${company.phone}` },
  { icon: Printer, label: "Fax", value: company.fax },
  { icon: Mail, label: "Email Us", value: company.email, href: `mailto:${company.email}` },
  { icon: Clock, label: "Business Hours", value: company.hours },
  { icon: MapPin, label: "Visit Us", value: company.address },
];

const socialItems = [
  { icon: Facebook, href: company.social.facebook, label: "Facebook" },
  { icon: Instagram, href: company.social.instagram, label: "Instagram" },
  { icon: Twitter, href: company.social.twitter, label: "Twitter" },
  { icon: Youtube, href: company.social.youtube, label: "Youtube" },
];

function ContactHero() {
  return (
    <section className="relative pt-44 pb-20 overflow-hidden">
      <div className="absolute inset-0">
        <img src="/images/epoxy.jpg" alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-zinc-950 via-zinc-950/90 to-zinc-950/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 to-transparent" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6">
        <span className="text-orange-500 font-semibold text-sm tracking-[0.2em] uppercase">Get in Touch</span>
        <h1 className="mt-3 font-display font-black text-white text-4xl md:text-6xl tracking-tight">Let's Build Something Great</h1>
        <p className="mt-4 text-zinc-300 text-lg max-w-2xl">Tell us about your project and we'll get back to you with a free consultation and quote.</p>
      </div>
    </section>
  );
}

function ContactInfo() {
  return (
    <Reveal className="lg:col-span-2">
      <div>
        <h2 className="font-display font-extrabold text-white text-2xl">Contact Information</h2>
        <p className="text-zinc-400 mt-3 leading-relaxed">
          Reach out by phone, email, or send us a message. We serve commercial, industrial and residential clients
          across Western New York.
        </p>
        <div className="mt-8 space-y-5">
          {infoItems.map((item) => (
            <div key={item.label} className="flex items-start gap-4">
              <span className="flex-shrink-0 w-11 h-11 rounded-lg bg-orange-500/15 flex items-center justify-center">
                <item.icon className="w-5 h-5 text-orange-400" />
              </span>
              <div>
                <div className="text-zinc-500 text-sm">{item.label}</div>
                {item.href ? (
                  <a href={item.href} className="text-white font-medium hover:text-orange-400 transition-colors">{item.value}</a>
                ) : (
                  <div className="text-white font-medium">{item.value}</div>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 mt-8">
          {socialItems.map((s) => (
            <a key={s.label} href={s.href} target="_blank" rel="noreferrer" aria-label={s.label} className="w-10 h-10 flex items-center justify-center rounded-md bg-zinc-900 text-zinc-400 hover:bg-orange-500 hover:text-white transition-colors">
              <s.icon className="w-4 h-4" />
            </a>
          ))}
        </div>
      </div>
    </Reveal>
  );
}

function ContactForm() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const error = validateForm(form);
    if (error) {
      toast.error(error);
      return;
    }
    setSubmitting(true);
    setTimeout(() => {
      recordInquiry(form);
      setSubmitting(false);
      setForm(initialForm);
      toast.success("Thanks! We've received your request and will be in touch shortly.");
    }, SUBMIT_DELAY_MS);
  };

  return (
    <Reveal className="lg:col-span-3" delay={120}>
      <form onSubmit={handleSubmit} className="rounded-2xl border border-zinc-800 bg-zinc-900 p-8">
        <div className="grid sm:grid-cols-2 gap-5">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-zinc-300">Full Name *</Label>
            <Input id="name" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="John Smith" className="bg-zinc-950 border-zinc-700 text-white h-11" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email" className="text-zinc-300">Email *</Label>
            <Input id="email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@company.com" className="bg-zinc-950 border-zinc-700 text-white h-11" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone" className="text-zinc-300">Phone</Label>
            <Input id="phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="(555) 555-5555" className="bg-zinc-950 border-zinc-700 text-white h-11" />
          </div>
          <div className="space-y-2">
            <Label className="text-zinc-300">Service Interested In</Label>
            <Select value={form.service} onValueChange={(v) => update("service", v)}>
              <SelectTrigger className="bg-zinc-950 border-zinc-700 text-white h-11">
                <SelectValue placeholder="Select a service" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-700 text-white">
                {services.map((s) => (
                  <SelectItem key={s.id} value={s.title} className="focus:bg-zinc-800 focus:text-orange-400">{s.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-2 mt-5">
          <Label htmlFor="message" className="text-zinc-300">Project Details *</Label>
          <Textarea id="message" value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Tell us about your space, square footage, timeline and goals..." rows={5} className="bg-zinc-950 border-zinc-700 text-white resize-none" />
        </div>
        <Button type="submit" disabled={submitting} className="mt-6 w-full h-12 bg-orange-500 hover:bg-orange-600 text-white font-semibold text-base">
          {submitting ? "Sending..." : (<>Send Message <Send className="w-4 h-4 ml-1" /></>)}
        </Button>
        <p className="text-zinc-600 text-xs text-center mt-4">We typically respond within one business day.</p>
      </form>
    </Reveal>
  );
}

export default function Contact() {
  return (
    <>
      <ContactHero />
      <section className="py-20 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-5 gap-10">
          <ContactInfo />
          <ContactForm />
        </div>
      </section>
    </>
  );
}
